// Shared Chart.js setup for the chart custom elements. Chart.js v4 is
// tree-shakable: only the pieces the donut + line charts actually use are
// registered here (line pieces register lazily in <line-chart>), so nothing
// else — bar/radar controllers, the built-in legend (legends here are
// server-rendered HTML) — lands in the bundle.
//
// Theme values are resolved from the CSS custom properties in application.css
// at first chart construction, so the palette stays single-sourced — the
// elements never hard-code a colour that exists as a --color-* variable.

import {
	ArcElement,
	CategoryScale,
	Chart,
	DoughnutController,
	LinearScale,
	Tooltip,
} from "chart.js";

let themed = false;

export function ensureChartTheme(): void {
	if (themed) return;
	themed = true;

	Chart.register(
		CategoryScale,
		LinearScale,
		DoughnutController,
		ArcElement,
		Tooltip,
	);

	// Axis ticks and tooltips take the body face (Work Sans), same as every
	// other small figure on the site.
	Chart.defaults.font.family =
		cssValue("--font-sans") || "ui-sans-serif, system-ui, sans-serif";
	Chart.defaults.font.size = 11;
	Chart.defaults.color = "rgba(255, 255, 255, 0.4)";
	Chart.defaults.animation = false;

	// Tooltip chrome matches the raised card surfaces.
	const tooltip = Chart.defaults.plugins.tooltip;
	tooltip.backgroundColor = cssValue("--color-surface") || "#121215";
	tooltip.borderColor = cssValue("--color-line") || "#232328";
	tooltip.borderWidth = 1;
	tooltip.titleColor = "#ffffff";
	tooltip.bodyColor = "rgba(255, 255, 255, 0.75)";
	tooltip.footerColor = "#ffffff";
	tooltip.padding = 10;
	tooltip.cornerRadius = 10;
	tooltip.caretSize = 5;
	tooltip.usePointStyle = true;
	tooltip.boxWidth = 7;
	tooltip.boxHeight = 7;
	tooltip.boxPadding = 4;
}

// Payload colours are CSS custom-property names ("--color-chart-1") so the
// server only ever speaks in palette tokens; anything not var-shaped passes
// through as a literal for one-off values.
export function chartColor(token: string): string {
	if (!token.startsWith("--")) return token;
	return cssValue(token) || "rgba(255, 255, 255, 0.4)";
}

// "#rrggbb" -> rgba() at the given alpha, for translucent takes on palette
// colours (the line chart's area fill). Non-hex values pass through.
export function withAlpha(color: string, alpha: number): string {
	const match = /^#([0-9a-f]{6})$/i.exec(color.trim());
	if (!match) return color;
	const int = parseInt(match[1], 16);
	return `rgba(${(int >> 16) & 255}, ${(int >> 8) & 255}, ${int & 255}, ${alpha})`;
}

function cssValue(name: string): string {
	return getComputedStyle(document.documentElement)
		.getPropertyValue(name)
		.trim();
}

// Money formatting mirrors Ui::Amount: compact for chrome (axis ticks),
// exact-to-the-pound for tooltips where the figure is the point of hovering.
// The corpus is summed as GBP (see RoundCharts on the currency
// simplification), so en-GB throughout.
export function money(
	value: number,
	currency: string,
	style: "exact" | "compact" = "exact",
): string {
	return new Intl.NumberFormat("en-GB", {
		style: "currency",
		currency,
		notation: style === "compact" ? "compact" : "standard",
		minimumFractionDigits: 0,
		maximumFractionDigits: style === "compact" ? 1 : 0,
	}).format(value);
}

// The server component renders chart data as a JSON script child — data in
// markup, behaviour in the element, same contract as the other enhanced
// elements. A missing script/canvas is a markup bug worth a loud error.
export function readPayload<T>(host: HTMLElement, tag: string): T {
	const script = host.querySelector<HTMLScriptElement>(
		'script[type="application/json"][data-chart-data]',
	);
	if (!script) throw new Error(`<${tag}>: missing data-chart-data script`);
	return JSON.parse(script.textContent || "null") as T;
}

export function findCanvas(host: HTMLElement, tag: string): HTMLCanvasElement {
	const canvas = host.querySelector("canvas");
	if (!canvas) throw new Error(`<${tag}>: missing canvas`);
	return canvas;
}
